"use client"

// SWEEPSTAKES (DEV-10671). Client-side only for the vv5 arm below: the header
// is on every page, so a request read here would make all of them dynamic.
import Cookies from "js-cookie"
import { useSyncExternalStore, type ReactNode } from "react"
import {
  announcementFor,
  announcementWithFreeShipping,
  isSweepstakesActive,
  type SweepstakesState,
} from "../../../lib/const/sweepstakes"
// [VV5-TEARDOWN] at teardown drop the cookie read, the enrolment check and the
// `channel` prop they need, and always append the clause.
import {
  VV5_COOKIE,
  isFreeShippingEnrolledChannel,
  shouldChargeShipping,
} from "@local/tracking/src/experiments/freeShipping"

const noopSubscribe = () => () => {}

interface SweepstakesTopbarProps {
  /** Market slug the header renders for. */
  market: string
  /** Channel slug of the page. Only an enrolled channel reads the vv5 arm. */
  channel: string
  /** Campaign state, resolved on the server. */
  scheduled: SweepstakesState
  /** The channel's own topbar, already resolved when it is a string. */
  fallback: ReactNode
  /** Free-shipping clause, resolved server-side. Omitted, the line stands alone. */
  freeShippingClause?: string
}

// Mirrors the string branch of TopPromoBanner so the campaign line and the
// channel's own copy look identical. Not imported from there: that module
// pulls in I18n and the shipping table, which have no business in this bundle.
const PROMO_CLASS =
  "flex-1 text-center text-xs leading-tight uppercase md:text-sm lg:flex-none"

export const SweepstakesTopbar = ({
  market,
  channel,
  scheduled,
  fallback,
  freeShippingClause,
}: SweepstakesTopbarProps) => {
  // [VV5-TEARDOWN] the test arm charges shipping, so it gets no free-shipping
  // clause. Same cookie read as Vv5Topbar, control as the server snapshot.
  //
  // INC-819: the cookie is set store-wide, so it speaks for an enrolled channel
  // only. On an unenrolled LP the visitor still ships free, and the clause
  // stays.
  const chargeShipping = useSyncExternalStore(
    noopSubscribe,
    () =>
      isFreeShippingEnrolledChannel(channel) &&
      shouldChargeShipping(Cookies.get(VV5_COOKIE)),
    () => false
  )
  const announcement = isSweepstakesActive(market, scheduled)
    ? announcementFor(scheduled)
    : null
  const line =
    announcement && freeShippingClause && !chargeShipping
      ? announcementWithFreeShipping(announcement, freeShippingClause)
      : announcement

  if (line) {
    return (
      <p className={PROMO_CLASS} dangerouslySetInnerHTML={{ __html: line }} />
    )
  }

  // No campaign line: the market keeps whatever its channel serves, which is
  // how `closed` and `winner` retire the banner.
  if (typeof fallback === "string") {
    return (
      <p
        className={PROMO_CLASS}
        dangerouslySetInnerHTML={{ __html: fallback }}
      />
    )
  }

  return <>{fallback}</>
}
